// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import {
    alertState,
    fieldPreviewData,
    actionFieldPreviewData,
    triggerConf,
    selectedField,
    lastFocusField,
    changedFields,
    saveAvailable,
    initialActions
} from "../../../../../../recoil-store/recoil-state";

// Import Material UI
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import { Button, OutlinedInput, Tooltip } from "@material-ui/core";
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import WarningIcon from '@material-ui/icons/Warning';
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import { Chip } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';
import { Autocomplete } from '@material-ui/lab';

// Import Pages
import ObjectField from './objectField/ObjectField';

// Import Services
import ApplicationService from '../../../../../../services/ApplicationService/ApplicationService';
import ActionService from '../../../../../../services/ActionService/ActionService';

// Import Helper
import { CheckIfFlowUpdated, getValueFromJSON } from '../../../../../../helpers/helper';

// Import Icon
import dataManagerIcon from '../../../../../../assets/icons/dataManager/data-manager-icon.svg';
import maximizeIcon from '../../../../../../assets/icons/dataManager/maximize-icon.svg';
import minimizeIcon from '../../../../../../assets/icons/dataManager/minimize-icon.svg';
import mediumIcon from '../../../../../../assets/icons/dataManager/medium-icon.svg';
import largeIcon from '../../../../../../assets/icons/dataManager/large-icon.svg';

// Import CSS
import './ConnectorField.css';

function ConnectorField({
    action,
    showFieldPreview,
    triggerConfig,
    setTriggerConf,
    debugOpen,
    debugExpand,
    triggerExpanded,
    onFlowPage,
    selectedCredential,
    automVersionId,
    automId,
    disabledFields,
    setDisabledFields,
    fieldLoading,
    rightLocation,
    variables,
    setOpenVariable,
    disableInputs,
    setSysRequiredFields,
    handleExpireToken,
    resizeClassName,
    automDetail,
    connectRequiredField,
    changedCredential
}) {
    const setSelectedField = useSetRecoilState(selectedField);
    const [actionLabel, setActionLabel] = useState('');
    const [showRequiredInputs, setShowRequiredInputs] = useState(false);
    const [showRequiredActionInputs, setShowRequiredActionInputs] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingFetchAction, setLoadingFetchAction] = useState({});

    const [triggerInputs, setTriggerInputs] = useState({});
    const [actionInputs, setActionInputs] = useState({});

    const [size, setSize] = useState('s');
    const [variableType, setVariableType] = useState('flow');
    const [searchText, setSearchText] = useState({});
    const [foundFields, setFoundFields] = useState({});
    const [requiredInputs, setRequiredInputs] = useState([]);
    const [triggerRequiredInputs, setTriggerRequiredInputs] = useState([]);
    const [isValid, setIsValid] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [triggerExpand, setTriggerExpand] = useState(triggerExpanded);
    const [sampleResponses, setSampleResponses] = useState({});
    const [fieldPrevData, setFieldPrevData] = useRecoilState(fieldPreviewData);
    const [actionFieldPrevData, setActionFieldPrevData] = useRecoilState(actionFieldPreviewData);
    // const automDetail = useRecoilValue(activeAutom);
    const [connector, setConnector] = useState({ id: '', trigger_action_id: '' });
    const focusField = useRecoilValue(lastFocusField);
    const [manuelMinimize, setManuelMinimize] = useState(false);
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const setChangeFields = useSetRecoilState(changedFields);
    const changedFieldsState = useRecoilValue(changedFields);

    const setAlert = useSetRecoilState(alertState);
    const labelFieldPreview = 'CONTENT.CONFIGURATION-FORM.TRIGGER-STATIC.FIELD-PREVIEW.'
    const labelFieldTrigger = 'CONTENT.CONFIGURATION-FORM.TRIGGER-STATIC.TRIGGERS.'
    const labelFieldPreviewMessages = 'MESSAGES.FIELD-PREVIEW.'

    const { t } = useTranslation();

    useEffect(() => {
        setSelectedField(f => ({
            ...f,
            key: '',
            name: '',
            type: '',
            path: []
        }))
    }, [])

    // Set initial states
    useEffect(() => {
        if (triggerConfig && triggerConfig.connector && triggerConfig.connector.id && (!onFlowPage || action)) {
            if (connector.id !== triggerConfig.connector.id || connector.trigger_action_id !== triggerConfig.trigger.trigger_action_id) {
                setLoading(true);
                setConnector({
                    id: triggerConfig.connector.id,
                    trigger_action_id: triggerConfig.trigger.trigger_action_id
                })
                setTriggerInputs(triggerConfig.setup.inputs ? triggerConfig.setup.inputs : {});
                setTriggerRequiredInputs([]);
                fetchTriggerData(triggerConfig.setup.inputs).then(r => r);
            } else {
                if (loading) {
                    setLoading(false);
                }
            }
        } else {
            if (loading) {
                setLoading(false);
            }
        }

    }, [triggerConfig]);

    // useEffect(() => {
    //     if (showFieldPreview) {
    //         if (size !== 'm') {
    //             setSize('m');
    //         }
    //     } else {
    //         if (size !== 's') {
    //             setSize('s');
    //         }
    //     }
    // }, [showFieldPreview])

    useEffect(() => {
        if (showFieldPreview) {
            if (focusField.inputName) {
                if (!manuelMinimize && size !== 'm') {
                    setSize('m');
                }
            } else {
                setSize('s');
            }
        }
    }, [focusField])

    useEffect(() => {
        if (onFlowPage && selectedCredential && action) {
            if (action.inputs) {
                //update state if app id has changed
                setActionInputs(JSON.parse(JSON.stringify(action.inputs)));
                if (!isValid) {
                    setIsValid(true);
                }
            } else {
                setActionInputs({});
            }
            if (requiredInputs?.length) {
                setRequiredInputs([]);
            }

            fetchActionFieldConfigurationData(action.inputs).then(r => {
                changeLoadingActions(action.item_id, false);
                if (changedCredential) {
                    setSize('m');
                } else {
                    setSize('s');
                }

            })

        }
    }, [selectedCredential])

    useEffect(() => {
        const fetchData = async () => {
            if (!action.item_id && action.action_name === 'STOP') {
                action.item_id = 'STOP'
            }

            const customActions = findCustomActions(automDetail.items, action.item_id, { found: false, c: [] });
            // get action_ids from customActions
            const action_ids = customActions.c.map(x => x.action_id);
            if (action_ids?.length) {
                const outputResponse = await ActionService.getCustomActionOutputs(action_ids, automVersionId);
                if (outputResponse.status) {
                    let outputs = [];
                    for (const item of customActions.c) {
                        let i = outputResponse.data.findIndex(x => x.action_id === item.action_id && x.action_item_id === item.uid)
                        if (i === -1) {
                            // default output
                            i = outputResponse.data.findIndex(x => x.action_id === item.action_id && x.action_item_id === null && x.autom_version_id === null)
                        }

                        if (i !== -1) {
                            let field_name = outputResponse.data[i].field_name;
                            outputs.push({
                                id: item.uid,
                                action_id: item.action_id,
                                action_name: outputResponse.data[i].action.action_name,
                                label: item.label,
                                field_name: item.fields[field_name] ? item.fields[field_name].value : '',
                                output: outputResponse.data[i].output,
                                application: outputResponse.data[i].action.application_action_field_configurations && outputResponse.data[i].action.application_action_field_configurations?.length
                                    ? outputResponse.data[i].action.application_action_field_configurations[0].application : null
                            })
                        }
                    }
                    setSampleResponses(outputs);
                }
            } else if (sampleResponses?.length) {
                setSampleResponses([]);
            }
        }
        if (action && onFlowPage) {
            fetchData().then(r => r);
        }
    }, [action?.item_id])

    // find custom actions before selected action
    function findCustomActions(actions, selectedId, customActions) {
        for (const item of actions) {
            if (item.item_id === selectedId) {
                customActions.found = true;
                // Label value of clicked action is kept
                setActionLabel(item.label);
                break
            }
            if ('children' in item) {
                const ac = findCustomActions(item.children, selectedId, customActions);
                // customActions.c = customActions.c.concat(ac)
                if (customActions.found) {
                    break;
                }
            } else if (item.libraryLabel && item.libraryLabel.includes('CUSTOMAUTOMMATE')) {
                customActions.c.push({ uid: item.item_id, action_id: item.actionId, fields: item.fields, label: item.label })
            }
        }
        return customActions
    }

    async function fetchTriggerData(inputs) {
        const response = await ApplicationService.getSetupFields({
            trigger_action_id: triggerConfig.trigger.trigger_action_id,
            app_credential_id: triggerConfig.connector.id,
            setup_fields: triggerConfig.setup.fields,
            inputs: inputs ? inputs : {}
        });

        if (response && response.status) {
            if (response.required) {
                setFieldPrevData({
                    ...fieldPrevData,
                    triggerData: {
                        data: {
                            field_path: {},
                            ff_name_path: {}
                        },
                        inputs: {},
                        inputConfigurations: [],
                        error: t(labelFieldPreviewMessages + response.message)
                    }
                })
                setShowRequiredInputs(true);
                setTriggerRequiredInputs(response.data.inputs);
                if (!triggerExpand) {
                    setTriggerExpand(true);
                }
                if (response.error) {
                    // keys refer to input value taken from the user and it includes source and target specifications.
                    // e.g. "inputs:request_id" || "setup_fields:id"
                    const keys = response.error.field.split(':');

                    setAlert(alert => ({
                        ...alert,
                        show: true,
                        message: t(labelFieldPreviewMessages + 'NO-DATA-EXISTS-RELATED-WITH-INPUT') + (keys.length > 1 ? keys[1] : keys[0]),
                        severity: 'error'
                    }))
                } else {
                    if (inputs && Object.keys(inputs).length) {
                        setAlert(alert => ({
                            ...alert,
                            show: true,
                            message: t(labelFieldPreviewMessages + 'NO-DATA-EXISTS-RELATED-WITH-INPUTS'),
                            severity: 'error'
                        }))
                        setIsValid(false);
                    }
                }

                // const requiredInput = response.required.field; // inputs:request_id
                // const requiredInputConfiguration = getValueFromJSON(response.data.inputs, "*.identifier", requiredInput.split(':')[1], "*");
                // setTriggerRequiredInputs(requiredInputConfiguration);
                // setShowRequiredInputs(true);
                // setTriggerExpand(true);
                // return undefined;
                //if (response.required && response.required) {

            } else {
                // setData(response.data.field_path);
                setFieldPrevData({
                    ...fieldPrevData,
                    triggerData: {
                        data: {
                            field_path: response.data.field_path,
                            ff_name_path: response.data.ff_name_path
                        },
                        inputConfigurations: response.data.inputs,
                        error: ''
                    }
                })
            }
        } else {
            setFieldPrevData({
                ...fieldPrevData,
                triggerData: {
                    data: {
                        field_path: {},
                        ff_name_path: {}
                    },
                    inputs: {},
                    inputConfigurations: [],
                    error: t(labelFieldPreviewMessages + response.message)
                }
            })

        }
        setLoading(false);
    }

    async function fetchActionFieldConfigurationData(inputs) {
        if (!loadingFetchAction[action.item_id]) {
            //setLoadingFetchAction(true);
            changeLoadingActions(action.item_id, true);
        }
        const response = await ApplicationService.getApplicationActionConfigurationFields({
            action_id: action.actionId,
            app_credential_id: selectedCredential.value,
            inputs: inputs ? inputs : {} //actionData ? actionData.inputs : {}
        });
        if (response) {
            let actionDataIndex = actionFieldPrevData.actionsData.findIndex(x => x.data.id === action.id);
            const actionsData = JSON.parse(JSON.stringify(actionFieldPrevData.actionsData));
            if (actionDataIndex === -1) {
                const newActionData = {
                    data: {
                        id: action.id,
                        item_id: action.item_id,
                        field_path: {},
                        ff_name_path: {}
                    },
                    additionalData: response.additionalData,
                    inputs: {},
                    inputConfigurations: {},
                    error: ''
                }


                actionsData.push(newActionData);
                actionDataIndex = actionsData.length - 1;
                // setActionFieldPrevData({
                //     ...actionFieldPrevData,
                //     actionsData
                // })
            } else if (!actionsData[actionDataIndex].data.item_id) {
                actionsData[actionDataIndex].data.item_id = action.item_id;
            }
            actionsData[actionDataIndex].error = '';
            if (response.status) {
                if (response.required) {
                    // if (response.required && response.required.field) {
                    // const requiredInput = response.required.field;
                    // const requiredInputConfiguration = getValueFromJSON(response.data.inputs, "*.identifier", requiredInput.split(':')[1], "*");
                    if (!showRequiredActionInputs[action.item_id]) {
                        changeShowRequiredInputs(action.item_id, true);
                        // setShowRequiredActionInputs({ [action.item_id]: true });
                    }
                    // if (!showRequiredActionInputs) {
                    //setShowRequiredActionInputs(true);
                    // }
                    setRequiredInputs(response.data.inputs);
                    if (response.error) {
                        // keys refer to input value taken from the user and it includes source and target specifications.
                        // e.g. "inputs:request_id" || "setup_fields:id"
                        const keys = response.error.field.split(':');

                        setAlert(alert => ({
                            ...alert,
                            show: true,
                            message: t(labelFieldPreviewMessages + 'NO-DATA-EXISTS-RELATED-WITH-INPUT') + (keys.length > 1 ? keys[1] : keys[0]),
                            severity: 'error'
                        }))
                    } else {
                        if (inputs && Object.keys(inputs).length) {
                            setAlert(alert => ({
                                ...alert,
                                show: true,
                                message: t(labelFieldPreviewMessages + 'NO-DATA-EXISTS-RELATED-WITH-INPUTS'),
                                severity: 'error'
                            }))
                        }
                    }

                    // setTriggerExpand(true);
                } else {
                    //setData(response.data.field_path);
                    actionsData[actionDataIndex].data.field_path = response.data.field_path;
                    actionsData[actionDataIndex].data.ff_name_path = response.data.ff_name_path;
                    actionsData[actionDataIndex].data.fp_details = response.data.fp_details;
                    actionsData[actionDataIndex].additionalData = response.additionalData;
                    actionsData[actionDataIndex].inputConfigurations = response.data.inputs;

                    //actionsData[actionDataIndex].data.display_names = response.data.display_names
                    // setActionFieldPrevData({
                    //     ...actionFieldPrevData,
                    //     actionsData: {
                    //         data: {
                    //             id: action.actionId,
                    //             field_path: response.data.field_path,
                    //             ff_name_path: response.data.ff_name_path
                    //         },
                    //         additionalData: response.additionalData,
                    //         inputConfigurations: response.data.inputs
                    //     }
                    // })
                    // setData(response.data.field_path);
                    changeShowRequiredInputs(action.item_id, false);
                    // if (showRequiredActionInputs) {
                    //setShowRequiredActionInputs(false);
                    // }

                    // The required fields of the action are loading
                    if (setSysRequiredFields && response.data.required_fields && Object.keys(response.data.required_fields).length) {
                        let required_fields = [];
                        for (const key in response.data.required_fields) {
                            const path = response.data.required_fields[key].path;
                            if (!action.fields.sys_action_fields?.value[path]) {
                                const value = {
                                    value: '',
                                    options: response.data.fp_details[key].options,
                                    type: response.data.fp_details[key].type,
                                    name: key,
                                    displayName: response.data.ff_name_path[key],
                                    instead_key: response.data.fp_details[key].instead_key,
                                    isRequired: true
                                }
                                required_fields.push({ path, value });
                            }
                        }
                        if (required_fields.length) {
                            // required fields are added to action sysFields and disabledFields
                            setSysRequiredFields(required_fields);
                        }
                    }
                }

                setActionFieldPrevData({
                    ...actionFieldPrevData,
                    actionsData
                })


                // setLoadingFetchAction(false);
            } else {
                if (response.message === 'OAUTH-REFRESH-TOKEN-EXPIRED' && handleExpireToken) {
                    handleExpireToken();
                }
                let errMessage = t(labelFieldPreviewMessages + response.message);
                if (response.message.includes('System Message:')) {
                    errMessage = response.message.split('System Message: ')[1];
                }

                actionsData[actionDataIndex].data.field_path = {};
                actionsData[actionDataIndex].data.ff_name_path = {};
                actionsData[actionDataIndex].data.fp_details = {};
                actionsData[actionDataIndex].inputConfigurations = {};
                actionsData[actionDataIndex].additionalData = response.additionalData;
                actionsData[actionDataIndex].error = errMessage;
                setActionFieldPrevData({
                    ...actionFieldPrevData,
                    actionsData
                })
                //  setLoadingFetchAction(false);
            }
        } else {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: "UNEXPECTED ERROR",
                severity: 'error'
            }))

            // setLoadingFetchAction(false);
        }

        if (!expanded[action.item_id]) {
            //setExpanded({ [action.item_id]: true })
            changeExpend(action.item_id, true);
            //setExpanded(true)
        }

    }

    const changeSize = (size) => {
        if (size) {
            setSize(size);
            if (size === 's') {
                setManuelMinimize(true);
            } else {
                setManuelMinimize(false);
            }
        }
    };

    const changeField = async (key, value, fromTrigger, isSelect) => {
        let fields;
        let v = true;
        if (!isSelect) {

            if (fromTrigger) {
                fields = { ...triggerInputs };
                fields[key] = value;
                for (const item of triggerRequiredInputs) {
                    if (!fields[item.identifier]) {
                        v = false;
                    }
                }
                setTriggerInputs(fields);
            } else {
                fields = { ...actionInputs };
                fields[key] = value;
                for (const item of requiredInputs) {
                    if (!fields[item.identifier]) {
                        v = false;
                    }
                }
                setActionInputs(fields);
            }
            setIsValid(v);
        } else {
            if (fromTrigger) {
                fields = { ...triggerInputs };
                fields[key] = value;
            } else {
                fields = { ...actionInputs };
                fields[key] = value;
            }

            checkAndClearDepends(key, fields);

            if (fromTrigger) {
                for (const item of triggerRequiredInputs) {
                    if (!fields[item.identifier]) {
                        v = false;
                    }
                }
                setTriggerInputs(fields);
            } else {
                for (const item of requiredInputs) {
                    if (!fields[item.identifier]) {
                        v = false;
                    }
                }
                setActionInputs(fields);
            }
            setIsValid(v);
            const resp = await ApplicationService.fetchDataManagerInputs({
                action_id: action.actionId,
                app_credential_id: selectedCredential.value,
                input_values: fields,
                dependency_name: key
            });
            if (resp.status && resp.data && resp.data.length) {
                let copyReq = [...requiredInputs];
                for (const inp of resp.data) {
                    let i = copyReq.findIndex(x => x.identifier === inp.identifier)
                    if (i > -1) {
                        copyReq[i] = inp;
                    }
                }

                setRequiredInputs(copyReq);

            }
        }

    }

    const checkAndClearDepends = (identifier, fields) => {
        let dependentInputs = requiredInputs.filter(x => x?.depends === identifier)
        if (dependentInputs && dependentInputs.length) {
            for (const input of dependentInputs) {
                if (fields[input.identifier]) {
                    fields[input.identifier] = "";
                }

                input.options = [];
                checkAndClearDepends(input.identifier, fields)
            }
        }
    }

    const saveRequiredInput = async (fromTrigger) => {
        if (isValid) {
            if (!fromTrigger) {
                let actionId
                if (action.id) {
                    actionId = action.id;
                }
                const field = initialActionsData[actionId]?.inputs;
                const oldValue = !field ? undefined : field;
                CheckIfFlowUpdated("connectorField", action, oldValue, actionInputs, setSaveAvailable, setChangeFields, changedFieldsState);
                //setShowRequiredActionInputs(false);
                connectRequiredField(actionInputs);
                await fetchActionFieldConfigurationData(actionInputs);
                action.inputs = JSON.parse(JSON.stringify(actionInputs));
                changeLoadingActions(action.item_id, false);
            } else {
                setLoading(true);
                const newSetup = JSON.parse(JSON.stringify(triggerConfig.setup));
                newSetup.inputs = triggerInputs;
                setShowRequiredInputs(false);
                await fetchTriggerData(triggerInputs)
                setTriggerConf(conf => ({
                    ...conf,
                    setup: newSetup,
                }))
            }
            // const response = await automService.updateTrigger({
            //     version_id: automVersionId,
            //     trigger_type: triggerConfig.trigger.type,
            //     trigger_config: {
            //         trigger_mode_id: triggerConfig.trigger.id,
            //         application_credential_id: triggerConfig.connector.id,
            //         trigger_setup: newSetup
            //     }
            // }, automId);

            // setAlert(alert => ({
            //     ...alert,
            //     show: true,
            //     message: t("MESSAGES.TRIGGER." + response.message),
            //     severity: response.status ? 'success' : 'error'
            // }))
            // if (response && response.status) {
            //     setRequiredInputs({});
            // }
        }
    }

    const foundInputDepends = (identifier, index) => {
        if (identifier) {
            const isLast = document.querySelector(".step-message")
            const dependsFound = requiredInputs.find((requiredInput) => requiredInput.depends === identifier)
            if (dependsFound || isLast) {
                return <small
                    className="help-text step-message">Step:  {index + 1}/{requiredInputs.length}</small>
            }
            return null
        }
    }
    const renderRequiredInputNew = (requiredInput, fromTrigger, index) => {
        const key = requiredInput.identifier;
        switch (requiredInput.type) {
            case 'string':
            case 'password':
            case 'email':
                if (requiredInput.depends) {
                    // if (!requiredInput.options?.length) {
                    //     return undefined
                    // }
                    if (fromTrigger) {
                        // debugger;
                        if (!triggerInputs[requiredInput.depends]) {
                            return undefined
                        }
                    }
                    else {
                        if (!actionInputs[requiredInput.depends]) {
                            // debugger;
                            return undefined
                        }
                    }
                }
                return (
                    <div className="form-element setup-element">
                        <div className="modal-text add-autom">
                            <div>{t(labelFieldPreview + requiredInput.display_name)}</div>
                        </div>
                        <TextField
                            autoComplete="off"
                            id="filled-size-small"
                            type={requiredInput.type}
                            value={fromTrigger ? triggerInputs[key] : actionInputs[key]}
                            onChange={(e) => changeField(key, e.target.value, fromTrigger, true)}
                            className={`required-input ${isValid ? '' : 'error-input'}`}
                            variant="outlined"
                            size="small"
                            inputProps={{
                                style: {
                                    fontSize: 15,
                                    paddingLeft: 15,
                                    backgroundColor: '#f7f7f7'
                                }
                            }}
                        />
                        {requiredInput.help_available && (
                            <small
                                className="help-text">{t(labelFieldPreview + requiredInput.display_name + '-HELP')}</small>
                        )}
                        {foundInputDepends(requiredInput.identifier, index)}
                    </div>
                )
            case 'single_select':
                if (requiredInput.depends) {
                    if (!requiredInput.options?.length) {
                        return undefined
                    }
                    if (fromTrigger) {
                        if (!triggerInputs[requiredInput.depends]) {
                            return undefined
                        }
                    } else {
                        if (!actionInputs[requiredInput.depends]) {
                            return undefined
                        }
                    }
                }
                return <div className="form-element setup-element">
                    <div className="modal-text"
                        style={{ marginBottom: 5 }}>{t(labelFieldPreview + requiredInput.display_name)}</div>
                    <FormControl
                        variant="outlined"
                        style={{ width: "100%" }}
                    >
                        {/* <Select
                            className={`${isValid ? '' : 'error-input'}`}
                            labelId="demo-simple-select-outlined-label"
                            style={{
                                backgroundColor: "#f7f7f7",
                                height: "41px",
                                fontSize: "15px",
                                paddingLeft: 15
                            }}
                            id="demo-simple-select-outlined"
                            matmazel={key}
                            value={fromTrigger ? triggerInputs[key] : actionInputs[key]}
                            onChange={(e) => changeField(key, e.target.value, fromTrigger)}
                        >
                            {requiredInput.options?.length > 0 &&
                                requiredInput.options?.map((item, index) => (
                                    <MenuItem
                                        style={{ fontSize: 12 }}
                                        key={index}
                                        value={item.value}
                                    >
                                        {item.name}
                                    </MenuItem>
                                ))}
                        </Select> */}
                        <Autocomplete
                            className={`${isValid ? '' : 'error-input'}`}
                            style={{
                                backgroundColor: "#f7f7f7",
                                height: "100%",
                                fontSize: "15px"
                            }}
                            size="small"
                            onChange={(event, item, reason) => changeField(key, item && item.value ? item.value : "", fromTrigger, true)}
                            value={fromTrigger ? triggerInputs[key] : actionInputs[key]}
                            id="demo-simple-select-outlined"
                            options={requiredInput.options?.length > 0 && JSON.stringify(requiredInput.options[0]) !== '{}' ? requiredInput.options : []}
                            getOptionLabel={(item) => {
                                if (item.name) {
                                    return item.name
                                }
                                let v = requiredInput.options?.find(x => x?.value === item);
                                if (v && v.name) {
                                    return v.name;
                                }
                                return item
                            }}
                            renderInput={(params) => <TextField {...params} variant="outlined" />}
                        />
                        {requiredInput.help_available && (
                            <small
                                className="help-text">{t(labelFieldPreview + requiredInput.display_name + '-HELP')}</small>
                        )}
                        {foundInputDepends(requiredInput.identifier, index)}
                    </FormControl>
                </div>
            default:
                return (<div />)
        }
    }

    // const renderRequiredInput = (requiredInput, fromTrigger) => {
    //     if (Object.keys(requiredInput).length > 0) {
    //         const key = requiredInput.identifier;
    //         switch (requiredInput.type) {
    //             case 'string':
    //             case 'password':
    //             case 'email':
    //                 return (
    //                     <div className="form-element setup-element">
    //                         <div className="modal-text add-autom">
    //                             <div>{t(labelFieldPreview + requiredInput.display_name)}</div>
    //                         </div>
    //                         <TextField
    //                             autoComplete="off"
    //                             id="filled-size-small"
    //                             type={requiredInput.type}
    //                             value={fromTrigger ? triggerInputs[key] : actionInputs[key]}
    //                             onChange={(e) => changeField(key, e.target.value, fromTrigger)}
    //                             className={`required-input ${isValid ? '' : 'error-input'}`}
    //                             variant="outlined"
    //                             size="small"
    //                             inputProps={{
    //                                 style: {
    //                                     fontSize: 15,
    //                                     paddingLeft: 15,
    //                                     backgroundColor: '#f7f7f7'
    //                                 }
    //                             }}
    //                         />
    //                         {requiredInput.help_available && (
    //                             <small
    //                                 className="help-text">{t(labelFieldPreview + requiredInput.display_name + '-HELP')}</small>
    //                         )}
    //                     </div>
    //                 )
    //             case 'single_select':
    //                 return <div className="form-element setup-element">
    //                     <div className="modal-text"
    //                         style={{ marginBottom: 5 }}>{t(labelFieldPreview + requiredInput.display_name)}</div>
    //                     <FormControl
    //                         variant="outlined"
    //                         style={{ width: "100%" }}
    //                     >
    //                         <Select
    //                             className={`${isValid ? '' : 'error-input'}`}
    //                             labelId="demo-simple-select-outlined-label"
    //                             style={{
    //                                 backgroundColor: "#f7f7f7",
    //                                 height: "41px",
    //                                 fontSize: "15px",
    //                                 paddingLeft: 15
    //                             }}
    //                             id="demo-simple-select-outlined"
    //                             matmazel={key}
    //                             value={fromTrigger ? triggerInputs[key] : actionInputs[key]}
    //                             onChange={(e) => changeField(key, e.target.value, fromTrigger)}
    //                         >
    //                             {requiredInput.options.length > 0 &&
    //                                 requiredInput.options?.map((item, index) => (
    //                                     <MenuItem
    //                                         style={{ fontSize: 12 }}
    //                                         key={index}
    //                                         value={item.value}
    //                                     >
    //                                         {item.name}
    //                                     </MenuItem>
    //                                 ))}
    //                         </Select>
    //                         {requiredInput.help_available && (
    //                             <small
    //                                 className="help-text">{t(labelFieldPreview + requiredInput.display_name + '-HELP')}</small>
    //                         )}
    //                     </FormControl>
    //                 </div>
    //             default:
    //                 return (<div />)
    //         }
    //     }
    // }

    const findField = (data, names, type, path, text, f, p) => {
        let exist = p;
        let searchText = text.toLowerCase();
        if (type === 'object') {
            for (const keyName in data) {
                let type = typeof data[keyName];

                let keyPath = path;
                keyPath += keyPath ? `[${keyName}]` : keyName;
                let displayName = names && names[keyName] ? names[keyName] : keyName;
                displayName = displayName.toLowerCase();
                let localExist = false;
                if (p || searchText && searchText.length > 1 && displayName.includes(searchText)) {
                    f[keyPath] = true;
                    exist = localExist = true;
                }
                if (Array.isArray(data[keyName])) {

                    if (data[keyName].length > 0) {
                        if (findField(data[keyName], names, 'array', keyPath, searchText, f, localExist)) {
                            f[keyPath] = true;
                            exist = true;
                        }
                    }
                } else if (type === 'object' && data[keyName]) {

                    if (findField(data[keyName], names, 'object', keyPath, searchText, f, localExist)) {
                        f[keyPath] = true;
                        exist = true;
                    }
                }
            }

        } else if (type === 'array') {
            for (const index in data) {
                const value = data[index];
                let newKeyPath = path;
                newKeyPath += `[${index}]`;
                let type = typeof value;
                if (Array.isArray(value)) {
                    if (value.length > 0) {
                        if (findField(value, names, 'array', newKeyPath, searchText, f, exist)) {
                            f[newKeyPath] = true;
                            exist = true;
                        }

                    }
                } else if (type === 'object' && value) {
                    if (findField(value, names, 'object', newKeyPath, searchText, f, exist)) {
                        f[newKeyPath] = true;
                        exist = true;
                    }
                }
            }
        }
        return exist;
    }

    function handleSearch(tv, item_id, v) {
        let f = {};
        if (tv.length > 1) {
            findField(v.data, v.names, v.type, '', tv, f, false);
            let ff = { ...foundFields }
            ff[item_id] = f
            setFoundFields(ff);
        }
        let st = { ...searchText };
        st[item_id] = tv;
        setSearchText(st);
    }

    const selectField = (name, type, path, key) => {
        if (key === 'triggerParam') {
            const names = fieldPrevData.triggerData.data.ff_name_path;
            name = names[name] ? names[name] : name;
        }
        setSelectedField(f => ({
            ...f,
            key,
            name,
            type,
            path: `##${path}##`,
        }))
    }

    const selectActionField = async (name, type, path) => {
        // const f = actionFieldPrevData.actionsData.find(x => x.data.id === action.actionId);
        // if (f) {

        // }
        //actionFieldPrevData.actionsData.data.ff_name_path
        //  const names = fieldPrevData.triggerData.data.ff_name_path;
        //name = names[name] ? names[name] : name;
        // setSelectedActionField(f => ({
        //     ...f,
        //     name,
        //     type,
        //     path: `##${path}##`
        // }))
        let actionDataIndex = actionFieldPrevData.actionsData.findIndex(x => x.data.id === action.id);
        let fType = type;
        let options;
        //let key;
        let instead_key;
        const displayName = actionFieldPrevData.actionsData[actionDataIndex].data.ff_name_path[name]
        if (actionFieldPrevData.actionsData[actionDataIndex].data.fp_details && actionFieldPrevData.actionsData[actionDataIndex].data.fp_details[name]) {
            fType = actionFieldPrevData.actionsData[actionDataIndex].data.fp_details[name].type;
            options = actionFieldPrevData.actionsData[actionDataIndex].data.fp_details[name].options;
            //key = actionFieldPrevData.actionsData[actionDataIndex].data.fp_details[name].key;
            instead_key = actionFieldPrevData.actionsData[actionDataIndex].data.fp_details[name].instead_key;

            // if (key) {
            //     path += `[${key}]`
            // }
        }
        setDisabledFields(path, { value: '', options, type: fType, name, displayName, instead_key, isRequired: true })
    }

    const changeExpend = (id, status) => {
        let copyData = { ...expanded };
        if (status !== undefined) {
            copyData[id] = status;
        } else {
            copyData[id] = !copyData[id];
        }
        setExpanded(copyData);
    }

    const changeShowRequiredInputs = (id, status) => {
        let copyData = { ...showRequiredActionInputs };
        copyData[id] = status;
        setShowRequiredActionInputs(copyData);
    }

    const changeLoadingActions = (id, status) => {
        let copyData = { ...loadingFetchAction };
        copyData[id] = status;

        setLoadingFetchAction(copyData);
    }

    const getVariable = (variable) => {
        if (variableType === 'system') {
            if (variable.system_variable) {
                return variable;
            }
        } else {
            if (!variable.system_variable) {
                return variable;
            }
        }
    }

    const resetActionInputs = async () => {
        setActionInputs({});
        let actionId
        if (action.id) {
            actionId = action.id;
        }
        const field = initialActionsData[actionId]?.inputs;
        const oldValue = !field ? undefined : field;
        CheckIfFlowUpdated("connectorField", action, oldValue, {}, setSaveAvailable, setChangeFields, changedFieldsState);
        action.inputs = {};
        await fetchActionFieldConfigurationData({});
        changeLoadingActions(action.item_id, false);
    }

    return (
        showFieldPreview ? (
            <div
                className={`field-preview-container ${size !== 'l' ? size : (debugOpen ? (debugExpand ? 'l-min' : 'l-expend') : 'l')} 
                bottom-${debugOpen ? (debugExpand ? '34' : '14') : rightLocation ? '3' : '2'} ${resizeClassName ? resizeClassName + `-${size}` : ""}`}>
                <div className="field-preview-header">
                    <div className="header-field">
                        <div className="field-preview-header-title">
                            <img src={dataManagerIcon} />
                            <span className='margin-left-10'>
                                {t(labelFieldPreview + "HEADER")}
                            </span>
                        </div>


                        {
                            size === 's' &&
                            <img className='s-shortcut size-icon' src={maximizeIcon} onClick={() => changeSize('m')} />
                        }
                        {
                            size === 'm' &&
                            <div className='m-l-shortcut'>
                                <img className='m-r-12 size-icon' src={largeIcon} onClick={() => changeSize('l')} />
                                <img className='size-icon' src={minimizeIcon} onClick={() => changeSize('s')} />
                            </div >

                        }
                        {
                            size === 'l' &&
                            <div className='m-l-shortcut'>
                                <img className='m-r-12 size-icon' src={mediumIcon} onClick={() => changeSize('m')} />
                                <img className='size-icon' src={minimizeIcon} onClick={() => changeSize('s')} />
                            </div>

                        }
                        {/* <Grid className="size" container spacing={2} direction="column" alignItems="flex-end">
                                <Grid item className="padding-0">
                                    <ToggleButtonGroup size="small" value={size} exclusive onChange={changeSize}>
                                        <ToggleButton value="s">
                                            <img src={maximizeIcon} />
                                        </ToggleButton>
                                        <ToggleButton value="m" className="toggle-icon">
                                            <img src={maximizeIcon} />
                                            <img src={minimizeIcon} />
                                        </ToggleButton>
                                        <ToggleButton value="l" className="toggle-icon">
                                            <img src={maximizeIcon} />
                                            <img src={minimizeIcon} />
                                        </ToggleButton>

                                    </ToggleButtonGroup>
                                </Grid>
                            </Grid> */}

                    </div>
                </div>
                <div className='field-preview-row'>
                    {
                        size !== 's' && (
                            <div className="padding-20 content margin-top-5">
                                {onFlowPage && // variable section
                                    <div className={`field-preview-data`}>
                                        <div className="field-preview-section">
                                            <div className="field-preview-item">
                                                {/* <div className="field-preview-item-icon">
                                                    <img src={triggerConfig.app?.image_path}
                                                        alt={triggerConfig.app?.name}
                                                        className="selected-trigger-app" />
                                                </div> */}
                                                <div className="field-preview-item-title">
                                                    <span className='content-title'>{t(labelFieldPreview + 'VARIABLES')}</span>
                                                    <Chip
                                                        className={`field-preview-title-chip ${variableType === 'flow' ? 'active' : 'pasif'}`}
                                                        variant="outlined"
                                                        size="small"
                                                        //color="default"
                                                        //onDelete={(e) => { }}
                                                        //color={variableType === 'flow' ? 'primary' : 'default'}
                                                        clickable
                                                        onClick={() => variableType !== 'flow' ? setVariableType('flow') : null}
                                                        // id={fieldData.id}
                                                        label={t(labelFieldPreview + "FLOW-VARIABLES")}
                                                    />
                                                    <Chip
                                                        className={`field-preview-title-chip ${variableType === 'system' ? 'active' : 'pasif'}`}
                                                        variant="outlined"
                                                        size="small"
                                                        // color={variableType === 'system' ? 'primary' : 'default'}
                                                        clickable
                                                        onClick={() => variableType !== 'system' ? setVariableType('system') : null}
                                                        //color="default"
                                                        //onDelete={(e) => handleDelete(e, item, filterTag, "Business process")}
                                                        // id={fieldData.id}
                                                        label={t(labelFieldPreview + "SYSTEM-VARIABLES")}
                                                    />
                                                </div>

                                                <div className="expand-fields"
                                                    onClick={() => changeExpend("variable_expend")}>
                                                    {expanded['variable_expend'] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </div>
                                            </div>
                                        </div>
                                        {expanded['variable_expend'] && (
                                            <div className="variable-section">
                                                <div className="category-library-title"
                                                    onClick={() => setOpenVariable({ type: "open", id: "" })}>
                                                    <div style={{ cursor: "pointer" }}>
                                                        <AddCircleOutlineIcon style={{ height: "14px" }} />
                                                        {t('CONTENT.AUTOMS-LIST.ADD-NEW-VARIABLES')}
                                                    </div>
                                                </div>
                                                <div className="search-area">
                                                    <SearchIcon />
                                                    <input className="search-text" placeholder="Search"
                                                        onChange={(e) => handleSearch(e.target.value, 'variable', {
                                                            data: variables,
                                                            names: {},
                                                            type: 'array'
                                                        })} />
                                                </div>
                                                {
                                                    variables?.filter(variable => {
                                                        if (!searchText['variable'] || (searchText['variable'] && searchText['variable'].length < 2)) {
                                                            if (variableType === 'system') {
                                                                if (variable.system_variable || variable.name === "triggerParam" || variable.name === "triggerHeader" || variable.name === "triggerQuery") {
                                                                    return variable;
                                                                }
                                                            } else {
                                                                if (!variable.system_variable && (variable.name !== "triggerParam" && variable.name !== "triggerHeader" || variable.name !== "triggerQuery")) {
                                                                    return variable;
                                                                }
                                                            }
                                                        } else if (variable.name.toLowerCase().includes(searchText['variable'].toLowerCase())) {
                                                            if (variableType === 'system') {
                                                                if (variable.system_variable || variable.name === "triggerParam" || variable.name === "triggerHeader" || variable.name === "triggerQuery") {
                                                                    return variable;
                                                                }
                                                            } else {
                                                                if (!variable.system_variable && (variable.name !== "triggerParam" && variable.name !== "triggerHeader" || variable.name !== "triggerQuery")) {
                                                                    return variable;
                                                                }
                                                            }
                                                        }

                                                    }).map(variable =>
                                                        <div className={`category-library-title`} key={variable.id}
                                                            style={{ paddingBottom: 0 }}>
                                                            <div className="padding-top-5 field-preview-button-container">
                                                                <Tooltip title={variable.type} placement="right-end" enterNextDelay={300} leaveDelay={0}
                                                                    PopperProps={{
                                                                        disablePortal: true,
                                                                    }}>
                                                                    <Button
                                                                        //disabled={disabledFields && (disabledFields[keyPath] || fp_details && fp_details[keyName] && fp_details[keyName].key && disabledFields[`${keyPath}[${fp_details[keyName].key}]`])}
                                                                        onClick={() => selectField(variable.name, variable.type, variable.name, '')}
                                                                        variant="contained"
                                                                        className="field-preview-button data-manager-button max-w-100"
                                                                        size="small"
                                                                    // startIcon={type === 'string' ? <ShortTextIcon /> : (type === 'number' ?
                                                                    //     <DialpadIcon /> : <EditAttributesIcon />)}
                                                                    >
                                                                        {/* {HTMLReactParser(variable.name)} */}
                                                                        <strong>[{variable.type}]</strong>
                                                                        <span className='field-preview-item-name'>{variable.name}</span>
                                                                    </Button>
                                                                </Tooltip>
                                                                {fieldLoading === variable.name ? <CircularProgress size={20} style={{ position: "absolute", left: "-25px", top: "10px" }} /> : ""}
                                                                {
                                                                    variable.default_value && (variable.type === 'array' || variable.type === 'object') && (
                                                                        <ObjectField
                                                                            root={true}
                                                                            selectField={(name, type, path) => selectField(name, type, path, variable.name)}
                                                                            data={JSON.parse(variable.default_value)}
                                                                            names={{}}
                                                                            hasParent={false}
                                                                            searchText={searchText['variable'] ? searchText['variable'] : ""}
                                                                            path={''}
                                                                            foundFields={foundFields['variable'] ? foundFields['variable'] : {}}

                                                                        />
                                                                    )
                                                                }
                                                            </div>
                                                            {/* <div>

                                                                <strong>[{variable.type}]</strong>
                                                                <label>{variable.name}</label>
                                                            </div> */}
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        )}
                                    </div>
                                }


                                {
                                    triggerConfig && triggerConfig.trigger && triggerConfig.trigger.name &&
                                    <div className={`field-preview-data`}>
                                        <div className="field-preview-section">
                                            <div className="field-preview-item">
                                                <div className="field-preview-item-icon">
                                                    <img src={triggerConfig.app?.image_path}
                                                        alt={triggerConfig.app?.name}
                                                        className="selected-trigger-app" />
                                                </div>
                                                <div className="field-preview-item-title">
                                                    <span className='content-title'>{t(labelFieldTrigger + triggerConfig.trigger.name)}</span>
                                                    <Chip
                                                        className="field-preview-title-chip pasif"
                                                        variant="outlined"
                                                        size="small"
                                                        //color="default"
                                                        //onDelete={(e) => handleDelete(e, item, filterTag, "Business process")}
                                                        // id={fieldData.id}
                                                        label={t(labelFieldPreview + "TRIGGER-DATA")}
                                                    />
                                                </div>
                                                {loading ? <CircularProgress style={{
                                                    width: '20px',
                                                    height: '20px'
                                                }} /> : triggerRequiredInputs?.length > 0 && showRequiredInputs ? (
                                                    <div className="required-fields">
                                                        <Tooltip
                                                            title={t(labelFieldPreview + "CLICK-TO-PROVIDE-REQUIRED-FIELD")}
                                                            placement="top">
                                                            <WarningIcon className="required-warning-icon" />
                                                        </Tooltip>
                                                    </div>
                                                ) : (
                                                    <div className="expand-fields"
                                                        onClick={() => setTriggerExpand(!triggerExpand)}>
                                                        {triggerExpand ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </div>
                                                )}

                                            </div>
                                            {triggerExpand && triggerRequiredInputs?.length === 0 && !fieldPrevData.triggerData.error && (
                                                <div className="field-preview-search">
                                                    <OutlinedInput
                                                        id="outlined-adornment-amount"
                                                        className="searchbar-input field-preview-searchbar-input"
                                                        value={searchText['trigger'] ? searchText['trigger'] : ''}
                                                        style={{ marginLeft: 0 }}
                                                        autoFocus={true}
                                                        onFocus={(event) => {
                                                            event.target.setAttribute('autocomplete', 'off');
                                                        }}
                                                        placeholder="Search..."
                                                        onChange={(event) => handleSearch(event.target.value, 'trigger', {
                                                            data: fieldPrevData.triggerData.data.field_path,
                                                            names: fieldPrevData.triggerData.data.ff_name_path,
                                                            type: 'object'
                                                        })}
                                                        startAdornment={<InputAdornment
                                                            position="start"><SearchIcon
                                                                className="searchbar-icon" /></InputAdornment>}

                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {triggerExpand && !loading && (
                                            triggerRequiredInputs?.length > 0 && showRequiredInputs ? (
                                                <div className="required-field-element">
                                                    <div className="form-element setup-element">
                                                        {triggerRequiredInputs.map((requiredInput, index) => (
                                                            renderRequiredInputNew(requiredInput, true, index)
                                                        ))}
                                                        <Button
                                                            disabled={!isValid}
                                                            className={`trigger-step-button required-field-save-btn ${isValid ? 'active' : 'passive'}`}
                                                            onClick={() => saveRequiredInput(true)}
                                                            variant="outlined"
                                                        >
                                                            {t('CONTENT.MODAL.SAVE')}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) :
                                                <div className="field-preview-root">
                                                    <div className="field-preview-content">
                                                        {
                                                            Object.keys(fieldPrevData.triggerData.data?.field_path)?.length > 0 ?
                                                                (
                                                                    <ObjectField root={false}
                                                                        selectField={(name, type, path) => selectField(name, type, path, 'triggerParam')}
                                                                        data={fieldPrevData.triggerData.data.field_path}
                                                                        names={fieldPrevData.triggerData.data.ff_name_path}
                                                                        hasParent={false}
                                                                        searchText={searchText['trigger'] ? searchText['trigger'] : ''}
                                                                        path={''}
                                                                        foundFields={foundFields['trigger'] ? foundFields['trigger'] : {}}
                                                                    />
                                                                )
                                                                : <Alert severity="error">{fieldPrevData.triggerData.error ? fieldPrevData.triggerData.error : ''}</Alert>
                                                        }

                                                    </div>
                                                </div>

                                        )}
                                    </div>

                                }
                                {
                                    onFlowPage && sampleResponses && sampleResponses?.length > 0 && !loading && sampleResponses.map((fieldData, index) =>

                                        <div className="field-preview-data" key={index}>
                                            <div className="field-preview-section">
                                                <div className="field-preview-item">
                                                    <div className="field-preview-item-icon">
                                                        <img
                                                            src={fieldData.application?.image_path}
                                                            alt={fieldData.application?.name}
                                                            className="selected-trigger-app" />
                                                    </div>
                                                    <div className="field-preview-item-title">
                                                        <span className='content-title'>{fieldData.label ? fieldData.label : fieldData.action_name}</span>
                                                        <Chip
                                                            className="field-preview-title-chip pasif"
                                                            variant="outlined"
                                                            size="small"
                                                            //color="primary"
                                                            //onDelete={(e) => handleDelete(e, item, filterTag, "Business process")}
                                                            id={fieldData.id}
                                                            label={t(labelFieldPreview + "RESPONSE-VALUES")}
                                                        />
                                                    </div>
                                                    <div className="expand-fields"
                                                        onClick={() => changeExpend('response_id:' + fieldData.id)}>
                                                        {expanded['response_id:' + fieldData.id] ? <ExpandLessIcon /> :
                                                            <ExpandMoreIcon />}
                                                    </div>

                                                </div>
                                                {expanded['response_id:' + fieldData.id] && Object.keys(fieldData.output).length > 0 && (
                                                    <div className="field-preview-search">
                                                        <OutlinedInput
                                                            id="outlined-adornment-amount"
                                                            className="searchbar-input field-preview-searchbar-input"
                                                            value={searchText[fieldData.id] ? searchText[fieldData.id] : ''}
                                                            style={{ marginLeft: 0 }}
                                                            autoFocus={true}
                                                            onFocus={(event) => {
                                                                event.target.setAttribute('autocomplete', 'off');
                                                            }}
                                                            placeholder="Search..."
                                                            onChange={(event) => handleSearch(event.target.value, fieldData.id, {
                                                                data: fieldData.output,
                                                                names: {},
                                                                type: 'object'
                                                            })}
                                                            startAdornment={<InputAdornment
                                                                position="start"><SearchIcon
                                                                    className="searchbar-icon" /></InputAdornment>}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            {expanded['response_id:' + fieldData.id] && (
                                                <div className="field-preview-root" >
                                                    <div className="field-preview-content">
                                                        {
                                                            !fieldData.field_name ? <Alert severity="error">{t(labelFieldPreview + 'OUTPUT-NOT-FOUND')}</Alert>
                                                                :
                                                                Object.keys(fieldData.output).length < 1 ?
                                                                    <Alert severity="info">{t(labelFieldPreview + 'NO-SAMPLE-RESPONSE')}</Alert>
                                                                    :
                                                                    <ObjectField root={false}
                                                                        selectField={(name, type, path) => selectField(name, type, path, fieldData.field_name)}
                                                                        data={fieldData.output}
                                                                        names={{}}
                                                                        hasParent={false}
                                                                        searchText={searchText[fieldData.id] ? searchText[fieldData.id] : ''}
                                                                        path={''} //disabledFields={disabledFields} fp_details={fieldData.data.fp_details}
                                                                        foundFields={foundFields[fieldData.id] ? foundFields[fieldData.id] : {}}
                                                                    />
                                                        }
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )
                                }

                                {
                                    action && onFlowPage && actionFieldPrevData.actionsData?.length > 0 && !loading && actionFieldPrevData.actionsData.map((fieldData, index) =>
                                        action.item_id === fieldData?.data?.item_id && <>
                                            <div className="field-preview-data" key={index}>
                                                <div className="field-preview-section">
                                                    <div className="field-preview-item">
                                                        <div className="field-preview-item-icon">
                                                            <img
                                                                src={fieldData.additionalData?.application?.image_path}
                                                                alt={fieldData.additionalData?.application?.name}
                                                                className="selected-trigger-app" />
                                                        </div>
                                                        <div className="field-preview-item-title">
                                                            <span className='content-title'>{actionLabel ? actionLabel : fieldData.additionalData?.action?.action_name}</span>
                                                            <Chip
                                                                className="field-preview-title-chip pasif"
                                                                variant="outlined"
                                                                size="small"
                                                                //color="primary"
                                                                //onDelete={(e) => handleDelete(e, item, filterTag, "Business process")}
                                                                id={fieldData.data.id}
                                                                label={t(labelFieldPreview + "INPUT-FIELDS")}
                                                            />
                                                        </div>

                                                        {
                                                            loadingFetchAction[fieldData.data.item_id] ?
                                                                <CircularProgress style={{ width: '20px', height: '20px' }} /> :
                                                                requiredInputs?.length > 0 && showRequiredActionInputs[action.item_id] ? (
                                                                    <div className="required-fields">
                                                                        <Tooltip
                                                                            title={t(labelFieldPreview + "CLICK-TO-PROVIDE-REQUIRED-FIELD")}
                                                                            placement="top">
                                                                            <WarningIcon className="required-warning-icon" />
                                                                        </Tooltip>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        {
                                                                            actionInputs && Object.keys(actionInputs).length > 0 &&
                                                                            <Button
                                                                                className='action-button-default reset-button'
                                                                                onClick={resetActionInputs}
                                                                                variant="outlined"
                                                                            >
                                                                                {t('CONTENT.MODAL.RESET')}
                                                                            </Button>
                                                                        }
                                                                        <div className="expand-fields"
                                                                            onClick={() => changeExpend(fieldData.data.item_id)}>
                                                                            {expanded[fieldData.data.item_id] ? <ExpandLessIcon /> :
                                                                                <ExpandMoreIcon />}
                                                                        </div>
                                                                    </>

                                                                )
                                                        }
                                                    </div>

                                                    {expanded[fieldData.data.item_id] && !showRequiredActionInputs[action.item_id] && !fieldData.error && Object.keys(fieldData.data.field_path).length > 0 &&
                                                        <div className="field-preview-search">

                                                            <OutlinedInput
                                                                id="outlined-adornment-amount"
                                                                className="searchbar-input field-preview-searchbar-input"
                                                                value={searchText[fieldData.data.item_id] ? searchText[fieldData.data.item_id] : ''}
                                                                style={{ marginLeft: 0 }}
                                                                autoFocus={true}
                                                                onFocus={(event) => {
                                                                    event.target.setAttribute('autocomplete', 'off');
                                                                }}
                                                                placeholder="Search..."
                                                                onChange={(event) => handleSearch(event.target.value, fieldData.data.item_id, {
                                                                    data: fieldData.data.field_path,
                                                                    names: fieldData.data.ff_name_path,
                                                                    type: 'object'
                                                                })}
                                                                startAdornment={<InputAdornment
                                                                    position="start"><SearchIcon
                                                                        className="searchbar-icon" /></InputAdornment>}

                                                            />
                                                        </div>
                                                    }
                                                </div>

                                                {expanded[fieldData.data.item_id] && !loadingFetchAction[fieldData.data.item_id] && (

                                                    requiredInputs?.length > 0 && showRequiredActionInputs[action.item_id] && fieldData.error === '' ? (
                                                        <div className="required-field-element">
                                                            <div className="form-element setup-element">
                                                                {requiredInputs.map((requiredInput, index) => (
                                                                    renderRequiredInputNew(requiredInput, false, index)
                                                                ))}
                                                                <Button
                                                                    disabled={!isValid} // requiredInputs.length !== Object.keys(actionInputs).length
                                                                    className={`trigger-step-button required-field-save-btn ${isValid ? 'active' : 'passive'}`}
                                                                    onClick={() => saveRequiredInput(false)}
                                                                    variant="outlined"
                                                                >
                                                                    {t('CONTENT.MODAL.CONNECT')}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="field-preview-root">
                                                            <div className="field-preview-content">
                                                                {
                                                                    fieldData.error !== '' ? (
                                                                        <Alert severity="error">{fieldData.error}</Alert>
                                                                    ) : Object.keys(fieldData.data.field_path).length > 0 ? (

                                                                        <ObjectField root={false}
                                                                            selectField={selectActionField}
                                                                            fieldLoading={fieldLoading}
                                                                            data={fieldData.data.field_path}
                                                                            names={fieldData.data.ff_name_path}
                                                                            hasParent={false}
                                                                            searchText={searchText[fieldData.data.item_id] ? searchText[fieldData.data.item_id] : ''}
                                                                            path={''}
                                                                            disabledFields={disabledFields}
                                                                            fp_details={fieldData.data.fp_details}
                                                                            disableInputs={disableInputs}
                                                                            foundFields={foundFields[fieldData.data.item_id] ? foundFields[fieldData.data.item_id] : {}}
                                                                        />
                                                                    ) : <Alert severity="info">{t(labelFieldPreview + "NO-INPUT-FIELD")}</Alert>
                                                                }
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        </>
                                    )
                                }
                            </div>
                        )}

                </div>
            </div>
        ) : null
    );
}

export default ConnectorField;
